import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Shield, Plus, Edit2, Trash2, Save, Users, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RolesPermissionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);

  const defaultPermissions = [
    { key: 'view_dashboard', label: 'View Dashboard', category: 'Dashboard' },
    { key: 'view_reports', label: 'View Reports', category: 'Reports' },
    { key: 'export_reports', label: 'Export Reports', category: 'Reports' },
    { key: 'manage_frameworks', label: 'Manage Frameworks', category: 'GRC' },
    { key: 'manage_controls', label: 'Manage Controls', category: 'GRC' },
    { key: 'manage_evidence', label: 'Manage Evidence', category: 'GRC' },
    { key: 'run_scans', label: 'Run Security Scans', category: 'Security' },
    { key: 'manage_accounts', label: 'Manage Cloud Accounts', category: 'Security' },
    { key: 'view_findings', label: 'View Findings', category: 'Security' },
    { key: 'manage_users', label: 'Manage Users', category: 'Admin' },
    { key: 'manage_settings', label: 'Manage Settings', category: 'Admin' },
    { key: 'manage_integrations', label: 'Manage Integrations', category: 'Admin' },
  ];

  const defaultRoles = [
    {
      id: 'owner',
      name: 'Owner',
      description: 'Full access to all features and settings',
      permissions: defaultPermissions.map(p => p.key),
      userCount: 1,
      isSystem: true
    },
    {
      id: 'admin',
      name: 'Administrator',
      description: 'Manage users, settings, and most features',
      permissions: defaultPermissions.filter(p => p.category !== 'Admin' || p.key !== 'manage_users').map(p => p.key),
      userCount: 2,
      isSystem: true
    },
    {
      id: 'analyst',
      name: 'Security Analyst',
      description: 'Run scans, view findings, manage evidence',
      permissions: ['view_dashboard', 'view_reports', 'run_scans', 'view_findings', 'manage_evidence'],
      userCount: 5,
      isSystem: false
    },
    {
      id: 'auditor',
      name: 'Auditor',
      description: 'Read-only access for compliance audits',
      permissions: ['view_dashboard', 'view_reports', 'export_reports', 'view_findings'],
      userCount: 3,
      isSystem: false
    },
    {
      id: 'viewer',
      name: 'Viewer',
      description: 'View-only access to dashboards and reports',
      permissions: ['view_dashboard', 'view_reports'],
      userCount: 8,
      isSystem: false
    }
  ];

  useEffect(() => {
    // Simulate loading roles
    setTimeout(() => {
      setRoles(defaultRoles);
      setLoading(false);
    }, 500);
  }, []);

  const handleEditRole = (role) => {
    setEditingRole({ ...role, permissions: [...role.permissions] });
    setShowEditDialog(true);
  };

  const togglePermission = (permKey) => {
    if (!editingRole) return;
    setEditingRole(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const handleSaveRole = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    setRoles(prev => prev.map(r => r.id === editingRole.id ? editingRole : r));
    toast.success('Role updated successfully');
    setShowEditDialog(false);
    setEditingRole(null);
    setSaving(false);
  };

  const permissionsByCategory = defaultPermissions.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="roles-permissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-7 h-7 text-indigo-500" />
            Roles & Permissions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure access levels and permissions for different user roles
          </p>
        </div>
        <Button disabled data-testid="create-role-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Role
        </Button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role) => (
          <Card key={role.id} className="relative" data-testid={`role-card-${role.id}`}>
            {role.isSystem && (
              <Badge variant="secondary" className="absolute top-3 right-3 text-xs">
                System
              </Badge>
            )}
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {role.name}
              </CardTitle>
              <CardDescription>{role.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Users
                  </span>
                  <Badge variant="outline">{role.userCount}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Permissions</span>
                  <Badge variant="outline">{role.permissions.length}</Badge>
                </div>
                <div className="flex flex-wrap gap-1">
                  {role.permissions.slice(0, 4).map(perm => (
                    <Badge key={perm} variant="secondary" className="text-xs">
                      {perm.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                  {role.permissions.length > 4 && (
                    <Badge variant="outline" className="text-xs">
                      +{role.permissions.length - 4} more
                    </Badge>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleEditRole(role)}
                  data-testid={`edit-role-${role.id}`}
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Permissions
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Role: {editingRole?.name}</DialogTitle>
            <DialogDescription>
              Configure permissions for this role. Changes will affect all users with this role.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-6">
            {Object.entries(permissionsByCategory).map(([category, perms]) => (
              <div key={category}>
                <h4 className="font-medium text-sm text-muted-foreground mb-3">{category}</h4>
                <div className="space-y-2">
                  {perms.map(perm => (
                    <div key={perm.key} className="flex items-center space-x-3">
                      <Checkbox
                        id={perm.key}
                        checked={editingRole?.permissions.includes(perm.key)}
                        onCheckedChange={() => togglePermission(perm.key)}
                        disabled={editingRole?.isSystem && perm.key === 'manage_users'}
                      />
                      <label
                        htmlFor={perm.key}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {perm.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRole} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPermissionsPage;
